import React, { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import WeekSelector from "../components/WeekSelector";
import './Dashboard.css';
import { TimesheetType } from "../interfaces/TimesheetInterface";
import { useSiteContext } from "../SiteContext";
import HomeManager from "../managers/HomeManager";
import EmployeeManager from "../managers/EmployeeManager";
import SupervisorManager from "../managers/SupervisorManager";
import { Page } from "../interfaces/PagesInterface";
import PageWrapper from "../components/PageWrapper";
import Colors from '../styles/Colors.json';
import config from "../configLoader";
import {darkenColor} from "../util/ColorUtil";
import SearchBar from "../components/SearchBar";
import { EmployeeSearchResult, EmployeeSearchTimesheetStatus, defaultEmployee } from "../interfaces/EmployeeInterface";

interface DashboardProps {
  setError: (error: string | null) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ setError }) => {
  const { userRole, supervisorQueryID, userName, setLoading, payPeriodID, setType, setPage, setSuperID, setSuperName, translator, setEmployee, setTimesheetID, setSuperAltID, createPopup } = useSiteContext();

  const [unsubmitted, setUnsubmitted] = useState(0);  // Unsubmitted timesheets count
  const [managerPending, setManagerPending] = useState(0);  // Manager pending timesheets count
  const [submitted, setSubmitted] = useState(0);  // Submitted but not yet approved timesheets count
  const [supervisorApproved, setSupervisorApproved] = useState(0);  // Timesheets approved by supervisor
  const [payrollApproved, setPayrollApproved] = useState(0);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchResults, setSearchResults] = useState<EmployeeSearchResult[]>([]);
  const [showSearchResults, setShowSearchResults] = useState<boolean>(false);

  // useEffect hook to fetch timesheet counts when the selected pay period changes
  useEffect(() => {
    const fetchTimesheetCounts = async () => {
      setLoading(true); // Set loading to true
      try {
        const userIdToPass = userRole.supervisor ? supervisorQueryID : null;
        
        // Fetch timesheet counts from API
        const counts = await HomeManager.getTimesheetCounts(payPeriodID, userIdToPass!);

        // Update the state with the fetched counts
        setUnsubmitted(counts.unsubmitted);
        setManagerPending(counts.managerPending);
        setSubmitted(counts.submitted);
        setSupervisorApproved(counts.supervisorApproved);
        setPayrollApproved(counts.payrollApproved);
      } catch (error) {
        console.error("Error fetching timesheet counts:", error); // Handle any errors
        setLoading(false);
      } finally {
         // Set loading to false after the data fetch
         setLoading(false);
      }
    };

    fetchTimesheetCounts(); // Call the async function to fetch counts
  }, [payPeriodID]);

  const setInfo = (supName: string, supID: number) => {

    setSuperName(supName);
    setSuperID(supID);

  }

  // Handler for when the "Unsubmitted Timesheet" button is clicked
  const handleUnsubmittedClick = () => {

    setType(TimesheetType.Unsubmitted);

    setPage(Page.SupervisorList);
  };  

  // Handler for when the "Manager Pending Timesheet" button is clicked
  const handleManagerPendingClick = () => {
    setType(TimesheetType.ManagerPending);
    setPage(Page.SupervisorList);
  };

  // Handler for when the "Supervisor Pending Timesheet" button is clicked
  const handleSubmittedClick = () => {
    setType(TimesheetType.Submitted);
    if (userRole.supervisor) {
      setInfo(userName, supervisorQueryID);
    }

    setPage(Page.SupervisorList);
  };

  // Handler for when the "Supervisor Approved Timesheet" button is clicked
  const handleSupApproveClick = () => {
    setType(TimesheetType.SuperApproved);
    if (userRole.supervisor) {
      setInfo(userName, supervisorQueryID);
    }
    
    setPage(Page.SupervisorList);
  };

  // Handler for when the "Payroll Approved Timesheet" button is clicked
  const handlePayApproveClick = () => {
    setType(TimesheetType.PayApproved);
    if (userRole.supervisor) {
      setInfo(userName, supervisorQueryID);
    }

    setPage(Page.SupervisorList);
  };

  // Handler for employee search
  const handleSearch = async () => {
    if (!searchTerm.trim() || !payPeriodID) {
      return;
    }

    setLoading(true);
    try {
      // Approval Access (WP8): the server scopes search results from the MSAL session,
      // so we no longer pass an adminUserEmployeeID / opt-in "see all" flag.
      const response = await EmployeeManager.searchEmployees(searchTerm.trim(), payPeriodID);
      setSearchResults(response.data || []);
      setShowSearchResults(true);
    } catch (error) {
      console.error("Error searching employees:", error);
      createPopup("Search Error", "Failed to search employees. Please try again.");
      setSearchResults([]);
      setShowSearchResults(false);
    } finally {
      setLoading(false);
    }
  };

  // Handler for Enter key press in search
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Map timesheet status to TimesheetType
  const mapStatusToType = (status: EmployeeSearchTimesheetStatus): TimesheetType => {
    switch (status) {
      case EmployeeSearchTimesheetStatus.Unsubmitted:
        return TimesheetType.Unsubmitted;
      case EmployeeSearchTimesheetStatus.FirstLevelApproval:
        return TimesheetType.ManagerPending;
      case EmployeeSearchTimesheetStatus.SecondLevelApproval:
        return TimesheetType.Submitted;
      case EmployeeSearchTimesheetStatus.PayrollPending:
        return TimesheetType.SuperApproved;
      case EmployeeSearchTimesheetStatus.PayrollApproved:
        return TimesheetType.PayApproved;
      default:
        return TimesheetType.Unsubmitted;
    }
  };

  // Get display name for timesheet status
  const getStatusDisplayName = (status: EmployeeSearchTimesheetStatus): string => {
    switch (status) {
      case EmployeeSearchTimesheetStatus.Unsubmitted:
        return translator.translate("UnsubmittedTimesheet").replace(":", "");
      case EmployeeSearchTimesheetStatus.FirstLevelApproval:
        return translator.translate("ManagerPendingTimesheet").replace(":", "");
      case EmployeeSearchTimesheetStatus.SecondLevelApproval:
        return translator.translate("SupervisorPendingTimesheet").replace(":", "");
      case EmployeeSearchTimesheetStatus.PayrollPending:
        return translator.translate("SupervisorApprovedTimesheet").replace(":", "");
      case EmployeeSearchTimesheetStatus.PayrollApproved:
        return translator.translate("PayrollApprovedTimesheet").replace(":", "");
      case EmployeeSearchTimesheetStatus.NoTimesheetForPayPeriod:
        return "No Timesheet for Week";
      default:
        return status;
    }
  };

  // Handler for clicking on a search result
  const handleEmployeeResultClick = async (result: EmployeeSearchResult) => {
    if (!payPeriodID || result.timesheetStatus === EmployeeSearchTimesheetStatus.NoTimesheetForPayPeriod) {
      return;
    }

    setLoading(true);

    try {
      const mappedType = mapStatusToType(result.timesheetStatus);
      setType(mappedType);

      let timesheetData;
      
      // Determine which API to call based on timesheet type
      if (mappedType === TimesheetType.Unsubmitted) {
        timesheetData = await SupervisorManager.getUnsubmittedEmployeeTimesheet(
          result.employeeID, 
          payPeriodID
        );
      } else {
        timesheetData = await SupervisorManager.getEmployeeTimesheet(
          result.employeeID, 
          payPeriodID
        );
      }
      
      // Update the context with the full employee data including timesheet info
      setEmployee({
        altEmployeeID: result.altEmployeeID,
        employeeID: result.employeeID,
        role: timesheetData.role || defaultEmployee.role,
        days: timesheetData.days || [],
        hourlyRate: timesheetData.hourlyRate || 0,
        jobTitle: timesheetData.jobTitle || '',
        phoneNumber: timesheetData.phoneNumber || '',
        name: result.name,
        managerApprovals: timesheetData.managerApprovals || [],
        superApprovals: timesheetData.superApprovals || [],
        payrollApprovals: timesheetData.payrollApprovals || [],
        approvalGroups: timesheetData.approvalGroups || [],
        timesheetID: timesheetData.timesheetID || 0,
      });

      if (timesheetData.timesheetID) { 
        setTimesheetID(timesheetData.timesheetID);
      } else {
        setTimesheetID(0);
      }
      
      // Set other required context values
      const supervisor = timesheetData.assignedSupervisor || timesheetData.timesheetSupervisor;

      if (supervisor) {
        setSuperID(supervisor.employeeID);
        setSuperAltID(supervisor.altEmployeeID || supervisor.employeeID.toString());
        setSuperName(`${supervisor.name.last} - ${supervisor.name.first}`);
      }
      
      // Navigate to TimesheetPage
      setPage(Page.TimeSheetPage);
      setShowSearchResults(false);
    } catch (error) {
      console.error("Error navigating to employee timesheet:", error);
      createPopup("Navigation Error", "Failed to load employee timesheet. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrapper title={translator.translate("Home")} backgroundColor={Colors.White} weekSelector={true}>
      <div className="timesheet-containers">
        {/* Employee Search Section */}
        <div className="employee-search-container">
          <div className="employee-search-bar-container">
            <SearchBar
              placeholder="Search by employee ID or name"
              value={searchTerm}
              onChange={setSearchTerm}
              onKeyPress={handleSearchKeyPress}
              style={{ maxWidth: '500px', minWidth: '300px' }}
            />
            <button 
              className="search-btn"
              onClick={handleSearch}
              disabled={!searchTerm.trim() || !payPeriodID}
            >
              Search
            </button>
          </div>
        </div>

        {/* Search Results Modal */}
        {showSearchResults && (
          <>
            {/* Overlay to dim the background */}
            <div className="employee-search-overlay" onClick={() => setShowSearchResults(false)} />
            
            {/* Modal Content */}
            <div className="employee-search-modal">
              <h2>Employee Search Results ({searchResults.length})</h2>
              {searchResults.length > 0 ? (
                <ul className="employee-search-results">
                  {searchResults.map((result, index) => (
                    <li
                      key={`${result.altEmployeeID}-${index}`}
                      className={`employee-search-result-item ${
                        result.timesheetStatus === EmployeeSearchTimesheetStatus.NoTimesheetForPayPeriod 
                          ? 'no-timesheet' 
                          : ''
                      }`}
                      onClick={() => {
                        if (result.timesheetStatus !== EmployeeSearchTimesheetStatus.NoTimesheetForPayPeriod) {
                          handleEmployeeResultClick(result);
                        }
                      }}
                    >
                      <div className="employee-search-result-name">
                        {result.name.first} {result.name.last} - {result.altEmployeeID}
                      </div>
                      <div className="employee-search-result-details">
                        <span>{result.location.name}</span>
                        <span className={`status-badge status-${result.timesheetStatus}`}>
                          {getStatusDisplayName(result.timesheetStatus)}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="no-results-message">No employees found matching your search.</p>
              )}
              <button className="employee-search-close-btn" onClick={() => setShowSearchResults(false)}>
                Close
              </button>
            </div>
          </>
        )}

        <div className="button-container">
          <button className="timesheet-btn" onClick={handleUnsubmittedClick}>
          {translator.translate("UnsubmittedTimesheet")} {unsubmitted}
          </button>
        </div>
        {config.allowManagerApproval && (
          <div className="button-container">
            <button className="timesheet-btn" onClick={handleManagerPendingClick}>
              {translator.translate("ManagerPendingTimesheet")} {managerPending}
            </button>
          </div>
        )}
        <div className="button-container">
          <button className="timesheet-btn" onClick={handleSubmittedClick}>
          {translator.translate("SupervisorPendingTimesheet")} {submitted}
          </button>
        </div>
        <div className="button-container">
          <button className="timesheet-btn" onClick={handleSupApproveClick}>
          {translator.translate("SupervisorApprovedTimesheet")} {supervisorApproved}
          </button>
        </div>
        <div className="button-container">
          <button className="timesheet-btn" onClick={handlePayApproveClick}>
          {translator.translate("PayrollApprovedTimesheet")} {payrollApproved}
          </button>
        </div>
      </div>
      
    </PageWrapper>
  );
};

export default Dashboard;
